import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';

import {
  AtoN,
  AtonApi,
  Configuration,
  type UserGroupUid,
} from '@ais-ui/api-spec/config';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { getBasePath } from '../getAISBasePath';
import { AtoNWithDate, GetAtoNs200ResponseWithDate } from '../types';

const useAtonApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const atonApi = useMemo(() => new AtonApi(configuration), [configuration]);
  return atonApi;
};

export const useFetchAllAtons =
  (): UseQueryResult<GetAtoNs200ResponseWithDate> => {
    const atonApi = useAtonApi();
    return useQuery({
      queryKey: ['FetchAllAtons'],
      queryFn: async () => {
        const result = await atonApi.getAtoNs();

        if (result) {
          convertFieldsToDates(result, []);
        }

        return result;
      },
    }, 'atons:list');
  };

export const useAddAton = (): UseMutationResult<
  UserGroupUid,
  Error,
  AtoN,
  unknown
> => {
  const atonApi = useAtonApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (aton: AtoN) => {
      return atonApi.addAtoN({ atoN: aton });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['FetchAllAtons'] }),
  }, 'aton:create');
};

type UpdateAtoNParams = {
  id: string;
  aton: AtoNWithDate;
};

export const useUpdateAton = (): UseMutationResult<
  void,
  Error,
  UpdateAtoNParams,
  unknown
> => {
  const atonApi = useAtonApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, aton }: UpdateAtoNParams) => {
      aton.uuid = id;
      return atonApi.updateAtoN({ uuid: id, atoN: aton as AtoN });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['FetchAllAtons'] }),
  }, 'aton:update');
};

export const useDeleteAton = (): UseMutationResult<
  void,
  Error,
  string,
  unknown
> => {
  const atonApi = useAtonApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      return atonApi.deleteAtoN({ uuid: id });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['FetchAllAtons'] }),
  }, 'aton:delete');
};
